import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Save, Loader2, Wallet, AlertTriangle } from 'lucide-react';

export function WalletSettings({ data, setData, isSaving, onSave }) {
  return (
    <Card data-testid="wallet-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Wallet className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <CardTitle>Wallet Settings</CardTitle>
            <CardDescription>Configure wallet top-up and balance requirements</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Enable/Disable Minimum Top-up */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="font-medium">Enforce Minimum Top-up</Label>
            <p className="text-sm text-gray-500 mt-1">
              When disabled, users can top up any amount (including ₱1).
            </p>
          </div>
          <Switch
            checked={data.enforce_min_topup !== false}
            onCheckedChange={(checked) => setData({ ...data, enforce_min_topup: checked })}
            data-testid="enforce-min-topup-switch"
          />
        </div>

        {/* Minimum Top-up Amount */}
        <div className={`space-y-2 ${data.enforce_min_topup === false ? 'opacity-50' : ''}`}>
          <Label htmlFor="min_topup_amount">Minimum Top-up Amount (PHP)</Label>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">₱</span>
            <Input
              id="min_topup_amount"
              type="number"
              min="1"
              max="10000"
              step="1"
              value={data.min_topup_amount || 100}
              onChange={(e) => setData({ ...data, min_topup_amount: parseInt(e.target.value) || 100 })}
              className="w-32"
              disabled={data.enforce_min_topup === false}
              data-testid="min-topup-input"
            />
          </div>
          <p className="text-sm text-gray-500">
            The minimum amount customers and providers can top up their wallets.
          </p>
        </div>

        {/* Enable/Disable Minimum Provider Balance */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div>
            <Label className="font-medium">Enforce Provider Minimum Balance</Label>
            <p className="text-sm text-gray-500 mt-1">
              When disabled, providers can accept bookings without balance requirements.
            </p>
          </div>
          <Switch
            checked={data.enforce_min_provider_balance !== false}
            onCheckedChange={(checked) => setData({ ...data, enforce_min_provider_balance: checked })}
            data-testid="enforce-min-balance-switch"
          />
        </div>

        {/* Minimum Balance for Providers */}
        <div className={`space-y-2 ${data.enforce_min_provider_balance === false ? 'opacity-50' : ''}`}>
          <Label htmlFor="min_provider_balance">Minimum Provider Balance (PHP)</Label>
          <div className="flex items-center gap-2">
            <span className="text-gray-500">₱</span>
            <Input
              id="min_provider_balance"
              type="number"
              min="0"
              max="10000"
              step="1"
              value={data.min_provider_balance || 100}
              onChange={(e) => setData({ ...data, min_provider_balance: parseInt(e.target.value) || 100 })}
              className="w-32"
              disabled={data.enforce_min_provider_balance === false}
              data-testid="min-provider-balance-input"
            />
          </div>
          <p className="text-sm text-gray-500">
            Providers must maintain this minimum balance to be visible to customers and accept bookings.
          </p>
        </div>

        {/* Quick Preset Amounts */}
        <div className="space-y-2">
          <Label>Quick Top-up Preset Amounts</Label>
          <div className="flex flex-wrap gap-2">
            {(data.topup_presets || [100, 500, 1000, 2000]).map((amount, idx) => (
              <div key={idx} className="flex items-center gap-1">
                <Input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => {
                    const newPresets = [...(data.topup_presets || [100, 500, 1000, 2000])];
                    newPresets[idx] = parseInt(e.target.value) || 100;
                    setData({ ...data, topup_presets: newPresets });
                  }}
                  className="w-24"
                  data-testid={`topup-preset-${idx}`}
                />
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500">
            These amounts appear as quick selection buttons on the top-up screen.
          </p>
        </div>

        {/* Preview */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
            <div>
              <h4 className="font-medium text-gray-900 text-sm">Current Configuration</h4>
              <ul className="text-sm text-gray-600 mt-1 space-y-1">
                <li>• Minimum top-up: {data.enforce_min_topup === false ? 'Disabled (any amount)' : `₱${data.min_topup_amount || 100}`}</li>
                <li>• Provider minimum balance: {data.enforce_min_provider_balance === false ? 'Disabled (no requirement)' : `₱${data.min_provider_balance || 100}`}</li>
                <li>• Quick amounts: {(data.topup_presets || [100, 500, 1000, 2000]).map(a => `₱${a}`).join(', ')}</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <Button 
          onClick={onSave} 
          disabled={isSaving}
          className="w-full"
          data-testid="save-wallet-settings-btn"
        >
          {isSaving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Wallet Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
